import { useLayoutEffect, useRef, useState } from "react";
import { useMeasureRef } from "lib/hooks/useMeasureRef";

// Model names often share their start (22085-BAC-EXG-ZZ-ZZ-M3-…) and differ
// at the end, so when space runs out, cut the middle and keep the ending.
const keptEndLength = 9;

/**
 * Cuts the middle of a name to whole characters. The browser's own ellipsis
 * leaves a sliver of space wherever the cut falls, which shows as a gap
 * before the kept ending, so the breadcrumb's fixed-width font lets us count
 * the characters that fit instead.
 */
export function cutMiddle(text: string, characterCount: number) {
  if (characterCount >= text.length) return text;
  if (characterCount < 4) return "…";
  const keptCount = characterCount - 1;
  const endLength =
    keptCount > keptEndLength ? keptEndLength : Math.ceil(keptCount / 2);
  return text.slice(0, keptCount - endLength) + "…" + text.slice(-endLength);
}

export default function BreadcrumbMiddleTruncatedText(p: { text: string }) {
  const fullTextRef = useRef<HTMLSpanElement>(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [shownText, setShownText] = useState(p.text);

  const setMeasureRef = useMeasureRef((bb) => setAvailableWidth(bb.width));

  useLayoutEffect(() => {
    const fullWidth = fullTextRef.current?.getBoundingClientRect().width;
    if (!fullWidth || !availableWidth) {
      setShownText(p.text);
      return;
    }
    const characterWidth = fullWidth / p.text.length;
    setShownText(
      cutMiddle(p.text, Math.floor(availableWidth / characterWidth + 0.01)),
    );
  }, [p.text, availableWidth]);

  return (
    <span
      ref={setMeasureRef}
      className="relative min-w-0 overflow-hidden"
      title={shownText !== p.text ? p.text : undefined}
    >
      {/* The full name, invisible, gives the crumb its natural width. */}
      <span
        ref={fullTextRef}
        aria-hidden
        className="invisible inline-block whitespace-pre"
      >
        {p.text}
      </span>
      <span className="absolute inset-0 whitespace-pre">{shownText}</span>
    </span>
  );
}
